import { DeleteOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  message,
  Modal,
  Space,
  Tooltip,
  Typography
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, {
  useCallback, useEffect, useMemo, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import {
  FilterDataProps, FilterValueProps,
  mappingFilterFields, mappingFilterToQuery, mappingParamsFilter, mappingQueryParamsFilter
} from 'common/components/PageTable/filter';
import { deleteBanksService, getAllBankService } from 'common/services/extends/banks';
import { ROUTE_PATHS } from 'common/utils/constant';

export type TableDataTypes = {
  id: number;
  // code?: string;
  name?: string;
  region?: string;
  branch?: string;
};

const BankManagement: React.FC<ActiveRoles> = ({
  roleIndex,
  roleDelete,
  roleCreate,
}) => {
  /* Hooks */
  const { t } = useTranslation();
  const navigate = useNavigate();

  /* Selectors */
  const {
    defaultPageSize, advancedFilter
  } = useAppSelector((state) => state.system);

  /* Search Params */
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => {
    const paramsObj = { ...Object.fromEntries(searchParams.entries()) };
    if (searchParams.has('page')) delete paramsObj.page;
    return paramsObj;
  }, [searchParams]);
  const pageParam = searchParams.get('page');
  const queryClient = useQueryClient();

  /* States */
  const [currentPage, setCurrentPage] = useState(Number(pageParam || 1));
  const [currentView, setCurrentView] = useState(defaultPageSize);
  const [selectedFilterList, setSelectedFilterList] = useState<
    FilterValueProps[]>(mappingQueryParamsFilter(params));
  const [keyword, setKeyword] = useState('');

  const isFirstRender = React.useRef(true);

  const returnParamsFilter = useMemo(
    () => mappingParamsFilter(selectedFilterList),
    [selectedFilterList]
  );

  const queryKey = ['bank-list', currentPage, currentView, selectedFilterList, keyword];

  /* Queries */
  const { data: listRes, isLoading } = useQuery(
    queryKey,
    () => getAllBankService({
      ...(keyword && { keyword }),
      page: currentPage,
      limit: currentView,
      ...returnParamsFilter
    }),
  );

  const { mutate: deleteMutate, isLoading: deleteLoading } = useMutation(
    ['banks-delete'],
    async (ids: number[]) => deleteBanksService({ ids }),
    {
      onSuccess: () => {
        message.success(t('message.deleteSuccess'));
        queryClient.invalidateQueries(queryKey);
      },
      onError: () => {
        message.error(t('message.deleteError'));
      }
    }
  );

  /* Datas */

  /* Variables */
  const columns: ColumnsType<TableDataTypes> = [
    // {
    //   title: t('system.code'),
    //   dataIndex: 'code',
    //   key: 'code',
    //   render: (_: string, data: TableDataTypes) => (
    //     <Typography.Text>
    //       {data.code}
    //     </Typography.Text>
    //   ),
    // },
    {
      title: t('system.name'),
      dataIndex: 'name',
      key: 'name',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.name}
        </Typography.Text>
      ),
    },
    {
      title: t('bank.region'),
      dataIndex: 'region',
      key: 'region',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.region}
        </Typography.Text>
      ),
    },
    {
      title: t('bank.branch'),
      dataIndex: 'branch',
      key: 'branch',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.branch}
        </Typography.Text>
      ),
    },
    {
      title: t('system.action'),
      key: 'action',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (_: string, data: TableDataTypes) => (
        <Space>
          <Tooltip title={t('system.viewDetail')}>
            <Button
              icon={<EyeOutlined />}
              disabled={!roleIndex}
              onClick={() => navigate(`${ROUTE_PATHS.BANK_DETAIL}?id=${data.id}`)}
            />
          </Tooltip>
          <Tooltip title={t('system.delete')}>
            <Button
              icon={<DeleteOutlined />}
              disabled={!roleDelete}
              onClick={(e) => {
                if (e.stopPropagation) e.stopPropagation();
                Modal.confirm({
                  className: 't-pagetable_deleteLanguageModal',
                  autoFocusButton: 'cancel',
                  okText: t('system.ok'),
                  cancelText: t('system.cancel'),
                  cancelButtonProps: {
                    type: 'primary',
                  },
                  okButtonProps: {
                    type: 'default',
                  },
                  title: t('message.confirmDeleteRecord'),
                  onOk: () => deleteMutate([data.id]),
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleSetCurrentPage = (page: number) => {
    setCurrentPage(page);
    setSearchParams({
      ...mappingFilterToQuery(selectedFilterList), page: page.toString()
    });
  };

  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  const filterFields: FilterDataProps[] = useMemo(
    () => mappingFilterFields('bank', advancedFilter),
    [advancedFilter]
  );

  const onDelete = async (data: TableDataTypes[], code?: string) => {
    switch (code) {
      case 'allRow':
      case 'all': {
        deleteMutate(data.map((item) => item.id));
        break;
      }
    }
  };

  const handleFilter = (data: FilterValueProps) => {
    const typeFilter = String(data.filter).split('.')[1];
    if ((typeFilter === 'isNull' || typeFilter === 'isNotNull') && selectedFilterList.find((item) => item.key === data.key)) {
      return;
    }
    const counter = selectedFilterList.filter(
      (item) => item.field === data.field && item.filter === data.filter
    ).length;
    setSelectedFilterList([...selectedFilterList, { ...data, index: counter }]);
  };

  const handleDeleteFilter = (key: string, index?: number) => {
    const tempList = selectedFilterList.slice();
    setSelectedFilterList(tempList.filter((item) => !(item.key === key && item.index === index)));
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setCurrentPage(1);
    setSearchParams({
      ...mappingFilterToQuery(selectedFilterList),
      page: '1'
    }, { replace: true });
  }, [keyword, setSearchParams, selectedFilterList]);

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(Number(pageParam));
    } else {
      setCurrentPage(1);
    }
  }, [pageParam]);

  const tableData: TableDataTypes[] = useMemo(() => {
    if (!listRes?.data) return [];

    return listRes.data.map((item) => ({
      id: item.bankData?.id,
      code: item.bankData?.code,
      name: item.bankData?.name,
      region: item.province?.name,
      branch: item.bankData?.branchName,
    }));
  }, [listRes]);

  const handleSearch = useCallback((val: string) => {
    setKeyword(val);
  }, []);

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.bank')}
        rightHeader={(
          <Button
            type="primary"
            disabled={!roleCreate}
            onClick={() => navigate(ROUTE_PATHS.BANK_DETAIL)}
          >
            <PlusOutlined />
            {t('system.create')}
          </Button>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <PageTable
          handleDelete={onDelete}
          handleSearch={handleSearch}
          isLoading={isLoading || deleteLoading}
          tableProps={{
            initShowColumns: ['name', 'region', 'branch', 'action'],
            columns,
            pageData: tableData || [],
            currentPage,
            pageSize: currentView,
            handleSetCurrentPage,
            handleSetCurrentView,
            total: listRes?.meta.total || 1,
            noBaseCol: true,
            noDeleteLanguage: true,
            filterFields
          }}
          filtersDataTable={{
            handleFilter,
            selectedFilterList,
            handleDeleteFilter,
          }}
        />
      </div>
    </>
  );
};

export default BankManagement;
